// --- OS detection (SSR-safe) -----------------------------------------------
export type OS = "android" | "ios" | "other";

export const getOS = (): OS => {
  if (typeof navigator === "undefined") return "other";

  const ua = navigator.userAgent || "";
  const platform =
    (navigator as any).userAgentData?.platform || navigator.platform || "";

  // iOS detection: iPhone/iPad/iPod, including iPadOS (which often reports as Mac)
  const isIOSUA = /iPad|iPhone|iPod/.test(ua);
  const isIPadOSDesktopUA =
    /Macintosh/.test(ua) &&
    "ontouchstart" in (typeof window !== "undefined" ? window : ({} as any));

  if (isIOSUA || isIPadOSDesktopUA) return "ios";

  // Android
  if (/Android/i.test(ua) || /Android/i.test(platform)) return "android";

  return "other";
};

// --- Installed (PWA or TWA) detection (SSR-safe) ---------------------------
export const isRunningAsPWA = (): boolean => {
  if (typeof window === "undefined") return false;

  // Desktop/Android installed PWA
  if (window.matchMedia?.("(display-mode: standalone)").matches) return true;

  // iOS standalone (Safari "Add to Home Screen")
  // @ts-ignore - not in TS lib
  if (typeof navigator !== "undefined" && (navigator as any).standalone)
    return true;

  return false;
};

export const isRunningAsTWA = (): boolean => {
  if (typeof window === "undefined") return false;

  // TWAs typically run as display-mode: fullscreen
  if (window.matchMedia?.("(display-mode: fullscreen)").matches) return true;

  // Some TWAs set android-app referrer
  if (
    typeof document !== "undefined" &&
    document.referrer?.startsWith("android-app://")
  )
    return true;

  return false;
};

export const isInstalled = (): boolean => isRunningAsPWA() || isRunningAsTWA();

// --- Prompt cooldown helper -------------------------------------------------
export const shouldShowInstallPrompt = (cooldownMs: number): boolean => {
  if (typeof window === "undefined") return false;
  try {
    const lastShown = localStorage.getItem("pwaPromptLastShown");
    const now = Date.now();
    if (lastShown && now - parseInt(lastShown, 10) < cooldownMs) return false;
    localStorage.setItem("pwaPromptLastShown", now.toString());
    return true;
  } catch {
    // If localStorage is blocked, don't spam: show only once per session
    return true;
  }
};
